import { JSX, Show } from 'solid-js';
import { t } from '@trimble/i18n';
import { useAuth } from '@trimble/trimble-id-solidjs';
import { useDropdown } from '@trimble/solidjs-utils';
import { UserAvatar } from './UserAvatar';

interface UserMenuProps {
  profileUrl: string;
}

export const UserMenu = (props: UserMenuProps): JSX.Element => {
  const { isOpen, toggle, close, ref } = useDropdown();
  const authState = useAuth();

  const handleProfileClick = (): void => {
    window.open(props.profileUrl, '_blank');
    close();
  };

  const handleLicenseCheck = (e: MouseEvent): void => {
    e.preventDefault();
    window.location.reload();
  };

  const handleLogout = (e: MouseEvent): void => {
    e.preventDefault();
    void authState().logout();
    close();
  };

  return (
    <div class="relative" ref={ref} data-testid="user-menu">
      <div
        class={`w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors duration-300 ${
          isOpen() ? 'bg-gray-0' : 'hover:bg-gray-0'
        }`}
        onClick={toggle}
        data-testid="user-avatar-button"
      >
        <UserAvatar
          picture={authState().user?.picture}
          name={authState().user?.name}
        />
      </div>
      <Show when={isOpen()}>
        <div
          class="absolute right-0 top-full -translate-x-2 w-[232px] bg-white rounded-sm py-2 z-dropdown shadow-soft"
          data-testid="user-dropdown"
        >
          <div
            class="px-4 py-2.5 border-b border-gray-1 text-center"
            data-testid="user-info-section"
          >
            <div class="flex justify-center mb-2">
              <UserAvatar
                picture={authState().user?.picture}
                name={authState().user?.name}
              />
            </div>
            <div
              class="text-[18px] text-gray-8 font-semibold cursor-default"
              data-testid="user-name"
            >
              {authState().user?.name || 'User'}
            </div>
            <div
              class="text-xs text-gray-4 cursor-default"
              data-testid="user-email"
            >
              {authState().user?.email || ''}
            </div>
            <div class="px-2 py-2">
              <modus-wc-button
                size="sm"
                onClick={handleProfileClick}
                data-testid="profile-button"
              >
                {t('general.header.myProfile')}
              </modus-wc-button>
            </div>
          </div>

          <div class="py-4 border-b border-gray-1">
            <a
              href="/"
              class="block w-full text-center px-4 py-2 text-sm text-blue-450 no-underline hover:bg-gray-0 font-semibold"
              onClick={handleLicenseCheck}
              data-testid="license-check-link"
            >
              {t('general.header.checkForUpdatedLicense')}
            </a>
          </div>
          <div class="pb-2 pt-4">
            <a
              href="/"
              class="block w-full text-center px-4 py-2 text-sm text-blue-450 no-underline hover:bg-gray-0 font-semibold"
              onClick={handleLogout}
              data-testid="sign-out-link"
            >
              {t('general.header.signOut')}
            </a>
          </div>
        </div>
      </Show>
    </div>
  );
};
